import { CalendarCheckIcon, CalendarXIcon } from '@phosphor-icons/react'
import { ColumnDef } from '@tanstack/react-table'
import { TreatmentStageCell } from 'app/components/action-center/TreatmentStageCell'
import { microMoney, nextVisit, procedureGroups, shortDate } from 'app/components/action-center/treatmentDisplay'
import type { TreatmentRow } from 'app/components/action-center/useTreatmentBoard'
import type { TreatmentColumnId, TreatmentDestination } from 'app/types/treatment'
import { Badge } from 'app/components/ui/badge'
import type { CategoryResolver } from 'lib/cdtCodeDefaults'
import { formatCurrency } from 'lib/formatCurrency'

export type TreatmentColumnHandlers = {
  /** Opens the patient's full contact record, as the case dialog's own action does. */
  onOpenRecord: (row: TreatmentRow) => void
  /** Writes a stage by its full name, exits included. */
  onSetStage: (row: TreatmentRow, stage: string) => void
  /** id -> practice name, for the hidden Location column. Absent until the query lands. */
  locationNames?: Map<string, { name: string }>
  /** The destinations the stage cell offers; the old pipeline's when absent. */
  stages?: TreatmentDestination[]
  /** The booked stage, shown read-only on a row that sits in it. */
  lockedStage?: TreatmentDestination
  /** The workspace's CDT mapping, which names each code's category. */
  categoryOf: CategoryResolver
}

/** Money, left-aligned like every other table's figures, and tabular so the digits still line up. */
function Money({ value, className }: { value: number; className?: string }) {
  return <span className={`tabular-nums ${className ?? ``}`}>{formatCurrency(value)}</span>
}

export const defaultColumns = ({
  onOpenRecord,
  onSetStage,
  locationNames,
  stages,
  lockedStage,
  categoryOf,
}: TreatmentColumnHandlers): (ColumnDef<TreatmentRow, any> & { id: TreatmentColumnId })[] => [
  {
    id: `Patient`,
    accessorKey: `name`,
    header: `Patient`,
    enableSorting: false,
    // The one column that cannot be hidden: a row with no name on it names
    // nobody, and every other column is about somebody.
    enableHiding: false,
    size: 220,
    meta: {
      defaultVisible: true,
      pinned: true,
      // The row itself opens the case; this opens the whole patient record,
      // the same pair of doors the case dialog offers. `onOpen`, not `onClick`:
      // the table draws `onOpen` as a labelled Open button and `onClick` as a
      // bare window icon that reads as "expand".
      onOpen: onOpenRecord,
    },
  },
  {
    id: `Unscheduled`,
    accessorFn: (row) => row.value,
    header: `Unscheduled`,
    size: 130,
    meta: {
      defaultVisible: true,
      // Amber is this number's colour everywhere on the board: it is the money
      // still needing a time on the schedule, never the plan total.
      ControlledComponent: (row) =>
        row ? (
          <div className="flex h-full items-center px-2">
            <Money value={row.value} className="font-medium text-amber-700" />
          </div>
        ) : (
          <></>
        ),
    },
  },
  {
    // The id predates the "Presented" header and stays: people's saved column settings key on it.
    id: `Planned`,
    accessorFn: (row) => row.presented,
    header: `Presented`,
    size: 130,
    meta: {
      defaultVisible: true,
      ControlledComponent: (row) => (
        <div className="flex h-full items-center px-2 text-default">
          {(row && shortDate(row.presented, true)) ?? `No date`}
        </div>
      ),
    },
  },
  {
    id: `Stage`,
    accessorFn: (row) => row.stage,
    header: `Stage`,
    enableSorting: false,
    size: 180,
    meta: {
      defaultVisible: true,
      ControlledComponent: (row) =>
        row ? (
          <TreatmentStageCell
            row={row}
            onSelect={(stage) => onSetStage(row, stage)}
            stages={stages}
            locked={lockedStage}
          />
        ) : (
          <></>
        ),
    },
  },
  {
    id: `Last visit`,
    accessorFn: (row) => row.lastVisitOn ?? ``,
    header: `Last visit`,
    enableSorting: false,
    size: 170,
    meta: {
      defaultVisible: true,
      // Paired with Next visit, the two read as one timeline: when they were
      // last in the chair, and when they are next. A break belongs on this
      // side of it, because that is what it is: the last appointment, and what
      // became of it. It used to have a column of its own headed "Reason",
      // which named a category rather than the question it answered.
      ControlledComponent: (row) => {
        const on = row && shortDate(row.lastVisitOn)
        return (
          <div className="flex h-full items-center px-2">
            {!on ? (
              // Not "never": the appointment read only reaches back so far
              // (see HISTORY_MONTHS in useBandCards), so this is the honest
              // limit of what was looked at.
              <span className="text-minimal">None in 18 months</span>
            ) : row?.lastVisitBroken ? (
              <Badge color="red" icon={<CalendarXIcon size={12} weight="fill" />} label={`Broken ${on}`} />
            ) : (
              <span className="text-default">{on}</span>
            )}
          </div>
        )
      },
    },
  },
  {
    id: `Next visit`,
    accessorFn: (row) => row.nextVisitOn ?? ``,
    header: `Next visit`,
    enableSorting: false,
    size: 180,
    meta: {
      defaultVisible: true,
      ControlledComponent: (row) => {
        const visit = row && nextVisit(row, categoryOf)
        const on = visit && shortDate(visit.on)
        return (
          <div
            className="flex h-full min-w-0 items-center px-2"
            title={visit ? [visit.word, on].filter(Boolean).join(` `) : undefined}
          >
            {visit ? (
              // Blue is treatment already on the books; grey is a cleaning,
              // which says they are reachable but none of this case is booked.
              <Badge
                color={visit.isTreatment ? `blue` : `gray`}
                icon={<CalendarCheckIcon size={12} weight="fill" />}
                label={
                  <span className="flex min-w-0 gap-1">
                    <span className="truncate">{visit.word}</span>
                    {` `}
                    {on && <span className="shrink-0">{on}</span>}
                  </span>
                }
                className="min-w-0 shrink"
              />
            ) : (
              <span className="text-minimal">Not booked</span>
            )}
          </div>
        )
      },
    },
  },
  {
    id: `Treatment`,
    accessorFn: (row) => procedureGroups(row, categoryOf)[0]?.label ?? ``,
    header: `Treatment`,
    enableSorting: false,
    size: 230,
    meta: {
      defaultVisible: true,
      ControlledComponent: (row) => {
        const groups = row ? procedureGroups(row, categoryOf) : []
        const [lead, ...rest] = groups
        const more = rest.reduce((count, group) => count + group.count, 0)
        return (
          <div className="flex h-full items-center gap-1.5 px-2">
            <span className="truncate text-default">{lead?.label ?? `Nothing outstanding`}</span>
            {more > 0 && <span className="shrink-0 text-minimal">+{more}</span>}
          </div>
        )
      },
    },
  },
  {
    id: `Phone`,
    accessorKey: `cellPhone`,
    header: `Phone`,
    enableSorting: false,
    size: 150,
  },
  {
    id: `Broken`,
    accessorFn: (row) => row.brokenValue,
    header: `Broken`,
    enableSorting: false,
    size: 120,
    meta: {
      ControlledComponent: (row) => (
        <div className="flex h-full items-center px-2 text-default">
          <Money value={row?.brokenValue ?? 0} />
        </div>
      ),
    },
  },
  {
    id: `Scheduled`,
    accessorFn: (row) => row.scheduledValue,
    header: `Scheduled`,
    enableSorting: false,
    size: 120,
    meta: {
      ControlledComponent: (row) => (
        <div className="flex h-full items-center px-2 text-default">
          <Money value={row?.scheduledValue ?? 0} />
        </div>
      ),
    },
  },
  {
    id: `Completed`,
    accessorFn: (row) => row.completedValue,
    header: `Completed`,
    enableSorting: false,
    size: 120,
    meta: {
      ControlledComponent: (row) => (
        <div className="flex h-full items-center px-2 text-default">
          <Money value={row?.completedValue ?? 0} />
        </div>
      ),
    },
  },
  {
    id: `Lifetime production`,
    accessorFn: (row) => row.historyValue,
    header: `Lifetime production`,
    enableSorting: false,
    size: 170,
    meta: {
      ControlledComponent: (row) => (
        <div className="flex h-full items-center gap-1.5 px-2 text-default">
          <span className="tabular-nums">{microMoney(row?.historyValue ?? 0)}</span>
          <span className="text-minimal">
            {row?.historyCount ?? 0} {row?.historyCount === 1 ? `visit` : `visits`}
          </span>
        </div>
      ),
    },
  },
  {
    id: `Location`,
    accessorFn: (row) => (row.locationId && locationNames?.get(row.locationId)?.name) ?? ``,
    header: `Location`,
    enableSorting: false,
    size: 180,
  },
]
